<template>
  <div class="home">
    <!-- Navigation bar -->
    <NavBar />
    
    <div class="main-container">
      <!-- Sidebar -->
      <nav class="sidebar">
        <div class="logo">
          <img src="../assets/images/logo.png" width="160px" alt="">
        </div>
        <ul class="category-list">
          <li 
            v-for="category in categories" 
            :key="category.id"
            :class="{ active: currentCategory === category.id }"
            @click="selectCategory(category.id)"
          >
            {{ category.name }}
          </li>
        </ul>
      </nav>

      <!-- Main content area -->
      <main class="main-content">
        <!-- Featured carousel -->
        <div class="featured-slider">
          <div class="featured-game" v-if="featuredGame">
            <img :src="featuredGame.thumbnail" :alt="featuredGame.title">
            <div class="featured-info">
              <h2>{{ featuredGame.title }}</h2>
              <p>{{ featuredGame.description }}</p>
              <button @click="playGame(featuredGame.id)">Play Now</button>
            </div>
          </div>
        </div>

        <!-- Game card grid -->
        <div class="game-grid">
          <GameCard
            v-for="game in visibleGames"
            :key="game.id"
            :game="game"
            @show-detail="showDetail(game.id)"
            @play-game="playGame(game.id)"
          />
        </div>
      </main>

      <!-- Sidebar info -->
      <aside class="info-sidebar">
        <div class="tabs">
          <button 
            v-for="tab in tabs" 
            :key="tab.id"
            :class="{ active: currentTab === tab.id }"
            @click="currentTab = tab.id"
          >
            {{ tab.name }}
          </button>
        </div>
        
        <div class="tab-content">
          <!-- Hot games -->
          <div v-if="currentTab === 'hot'" class="ranking-section">
            <h3>Hot Games</h3>
            <ul class="ranking-list">
              <li v-for="(game, index) in hotGames" :key="game.id">
                <span class="rank">{{ index + 1 }}</span>
                <span class="title">{{ game.title }}</span>
              </li>
            </ul>
          </div>
          
          <!-- Featured games -->
          <div v-if="currentTab === 'featured'" class="ranking-section">
            <h3>Featured Games</h3>
            <ul class="ranking-list">
              <li v-for="(game, index) in featuredGames" :key="game.id">
                <span class="rank">{{ index + 1 }}</span>
                <span class="title">{{ game.title }}</span>
              </li>
            </ul>
          </div>
          
          <!-- Recent games -->
          <div v-if="currentTab === 'recent'" class="ranking-section">
            <h3>Recent Games</h3>
            <ul class="ranking-list">
              <li v-for="(game, index) in recentGames" :key="game.id">
                <span class="rank">{{ index + 1 }}</span>
                <span class="title">{{ game.title }}</span>
              </li>
            </ul>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useGameStore } from '../stores/gameStore'
import GameCard from '../components/GameCard.vue'
import NavBar from '../components/NavBar.vue'

const router = useRouter()
const store = useGameStore()

const categories = [
  { id: 'all', name: 'All Games' },
  { id: 'classic', name: 'Classic' }, 
  { id: 'casual', name: 'Casual' }, 
  { id: 'strategy', name: 'Strategy' }, 
  { id: 'puzzle', name: 'Puzzle' }, 
  { id: 'action', name: 'Action' }, 
  { id: 'rpg', name: 'RPG' }, 
  { id: 'racing', name: 'Racing' }, 
  { id: 'card', name: 'Card' } 
]

const tabs = [
  { id: 'hot', name: 'Hot Games' },
  { id: 'featured', name: 'Featured Games' },
  { id: 'recent', name: 'Recent Games' }
]

const currentCategory = computed(() => store.currentCategory)
const currentTab = ref('hot')
const visibleGames = computed(() => store.visibleGames)
const hotGames = computed(() => store.hotGames)
const featuredGame = computed(() => store.games[0])

// 精品榜（按评分排序）
const featuredGames = computed(() => {
  return [...store.games]
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 5)
})

// 最近游戏（按ID倒序，模拟最近添加）
const recentGames = computed(() => {
  return [...store.games]
    .sort((a, b) => b.id - a.id)
    .slice(0, 5)
})

onMounted(() => {
  store.loadGames()
})

const selectCategory = (categoryId) => {
  store.currentCategory = categoryId
  store.currentPage = 1
}

const showDetail = (gameId) => {
  router.push(`/game/${gameId}`)
}

const playGame = (gameId) => {
  router.push(`/play/${gameId}`)
}
</script>

<style scoped>
.home {
  min-height: 100vh;
  background: #f5f5f5;
}

.main-container {
  display: grid;
  grid-template-columns: 200px 1fr 300px;
  min-height: calc(100vh - 60px);
  padding: 20px;
  gap: 20px;
}

.sidebar {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.logo h1 {
  color: #1E90FF;
  font-size: 24px;
  margin-bottom: 30px;
  text-align: center;
}

.category-list {
  list-style: none;
  padding: 0;
}

.category-list li {
  padding: 12px;
  margin: 4px 0;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.category-list li:hover {
  background: #E6E6FA;
}

.category-list li.active {
  background: #1E90FF;
  color: white;
}

.main-content {
  padding: 20px;
}

.featured-slider {
  margin-bottom: 30px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.featured-game {
  position: relative;
  height: 300px;
}

.featured-game img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.featured-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background: linear-gradient(transparent, rgba(0,0,0,0.8));
  color: white;
}

.game-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  padding: 20px;
}

.info-sidebar {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

.tabs {
  display: flex;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
}

.tabs button {
  flex: 1;
  padding: 12px;
  border: none;
  background: none;
  cursor: pointer;
  font-weight: bold;
  color: #666;
  transition: all 0.3s ease;
}

.tabs button:hover {
  color: #1E90FF;
}

.tabs button.active {
  color: #1E90FF;
  border-bottom: 2px solid #1E90FF;
}

.tab-content {
  padding: 20px;
}

.ranking-section {
  margin-top: 10px;
}

.ranking-list {
  list-style: none;
  padding: 0;
}

.ranking-list li {
  display: flex;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #eee;
}

.rank {
  width: 24px;
  height: 24px;
  background: #1E90FF;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
}

.title {
  color: #666;
}
</style> 